import {
  CUSIPS_LIST_ARRAY_NAME,
} from '@/finsight/ui/components/portfolioMonitor/PortfolioContent/PortfolioForm/FormSteps/CreateListsForm/constants';
import {
  defaultPositionValues,
} from '@/finsight/ui/components/portfolioMonitor/PortfolioContent/PortfolioForm/constants';
import { v4 as uuid } from 'uuid';
import {
  IGetBondsByCusipsResponse,
  IGetBondsByTrancheIdsResponse,
} from '@/finsight/domain/vo/PortfolioMonitor/ICreatePortfolioResponse';
import { IPortfolioFormList, IPosition } from '@/finsight/domain/vo/PortfolioMonitor/ICreatePortfolioFormState';

const normalizePositions = (positions) => positions.flatMap(({ value, cusip, ...otherProps }) => ([{
  cusip: value || cusip, localId: uuid(), ...otherProps,
}])).map((item) => ({ ...defaultPositionValues, ...item }));

export const getPositionsFromFieldValues = (values) => values[CUSIPS_LIST_ARRAY_NAME].reduce(
  (acc, { id, name, positions, isLocal }, index) => [...acc,
    {
      id: isLocal ? null : id,
      name,
      priority: index + 1,
      positions: normalizePositions(positions),
    },
  ],
[]);

interface IErrorData {
  [key: string]: {
    cusip: string,
  },
}

const extractCusipsFromErrors = (errorData: IErrorData) => {
  const uniqueCusips = new Set();

  Object.values(errorData).forEach((error) => {
    const [, cusipFromErrorMessage] = error.cusip.split('invalid cusip: ');
    const decodedCusip = decodeURIComponent(cusipFromErrorMessage);
    if (decodedCusip) {
      uniqueCusips.add(decodedCusip);
    }
  });

  return uniqueCusips;
};

export const getValidatedLists = (values, errorData: IErrorData) => {
  const cusipsFromErrors = extractCusipsFromErrors(errorData);
  const updatedLists = values[CUSIPS_LIST_ARRAY_NAME].map((list) => {
    const positions = list.positions.map((position) => {
      const isErrorForCusip = cusipsFromErrors.has(position.value);
      return {
        ...position,
        isValid: !isErrorForCusip,
      };
    });

    return {
      ...list,
      positions,
    };
  });

  return updatedLists;
};

export const transformErrorDataToValidateCusipsData = (errorData) => {
  const validateCusipsData = [];

  Object.keys(errorData).forEach((key) => {
    const match = key.match(/(\d+)/);

    if (match) {
      const index = Number(match[1]);
      const [, cusipFromErrorMessage] = errorData[key].cusip.split('invalid cusip: ');
      const decodedCusip = decodeURIComponent(cusipFromErrorMessage);
      validateCusipsData.push({
        cusip: decodedCusip,
        fieldName: `cusipLists[${ index }].cusips`,
      });
    }
  });

  return validateCusipsData;
};

const updatePositionByCusip = (position: IPosition, response: IGetBondsByCusipsResponse) => {
  const bonds = response[position.cusip];
  const bondForCusip = bonds?.length === 1 ? bonds[0] : null;

  if (bondForCusip === null) {
    return { ...position, isBondAutoSelected: false };
  }

  return {
    ...position,
    bond: { ...position.bond, ...bondForCusip, customBondName: null },
    isBondAutoSelected: true,
  };
};

const updateListPositionsByCusips = (list: IPortfolioFormList, response: IGetBondsByCusipsResponse) => ({
  ...list,
  positions: list.positions.map((position) => updatePositionByCusip(position, response)),
});

// updatePayloadFromCusipsResponse is using for merging checked (not mapped) cusips with positions in form payload
export const updatePayloadFromCusipsResponse = (response: IGetBondsByCusipsResponse, setPayload) => setPayload(
    (prevState) => ({
    ...prevState,
    lists: prevState.lists.map((list) => updateListPositionsByCusips(list, response)),
  }),
);

const updatePositionByTrancheId = (position, response: IGetBondsByTrancheIdsResponse) => {
  const bond = response[position.bond?.trancheId];
  return bond
    ? ({
      ...position,
      bond: { ...bond, customBondName: null },
      isBondAutoSelected: true,
    })
    : position;
};

const updateListByTrancheId = (list: IPortfolioFormList, response: IGetBondsByTrancheIdsResponse) => ({
  ...list,
  positions: list.positions.map((position) => updatePositionByTrancheId(position, response)),
});

// updatePayloadFromTranchesResponse is using for merging manually mapped cusips on some other tranche with form payload
export const updatePayloadFromTranchesResponse = (response: IGetBondsByTrancheIdsResponse, setPayload) => setPayload(
  (prevState) => ({
    ...prevState,
    lists: prevState.lists.map((list) => updateListByTrancheId(list, response)),
  }),
);
